import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';

const ThemeContext = createContext({
  mode: 'dark',           // user's choice: 'system' | 'light' | 'dark'
  resolved: 'dark',       // what's actually applied: 'light' | 'dark'
  setMode: () => {},
  toggleMode: () => {},
});

const STORAGE_KEY = 'ht_theme';

function readStoredMode() {
  try {
    if (typeof window === 'undefined') return 'dark';
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'system' || stored === 'light' || stored === 'dark') return stored;
    return 'dark';
  } catch {
    return 'dark';
  }
}

function getSystemTheme() {
  try {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
    }
  } catch { /* ignore */ }
  return 'dark';
}

/**
 * ThemeProvider
 * - mode = user's choice ('system' | 'light' | 'dark')
 * - resolved = actual theme applied ('light' | 'dark')
 *   When mode === 'system', resolved follows prefers-color-scheme and updates live.
 * - Default mode: 'dark'
 * - Persistence: localStorage only.
 */
export function ThemeProvider({ children }) {
  const [mode, setModeState] = useState(readStoredMode);
  const [systemTheme, setSystemTheme] = useState(getSystemTheme);

  // Watch for OS dark/light changes
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const handler = (e) => setSystemTheme(e.matches ? 'light' : 'dark');
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else if (mq.addListener) mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else if (mq.removeListener) mq.removeListener(handler);
    };
  }, []);

  const resolved = mode === 'system' ? systemTheme : mode;

  // Apply body class + persist
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.body.classList.remove('ht-theme-light', 'ht-theme-dark');
    document.body.classList.add(resolved === 'light' ? 'ht-theme-light' : 'ht-theme-dark');
    // Toggle the standard Tailwind `dark` class on <html> so `dark:*` utilities work
    if (document.documentElement) {
      if (resolved === 'dark') document.documentElement.classList.add('dark');
      else document.documentElement.classList.remove('dark');
    }
  }, [resolved]);

  useEffect(() => {
    try { window.localStorage.setItem(STORAGE_KEY, mode); } catch { /* ignore */ }
  }, [mode]);

  const setMode = useCallback((next) => {
    if (next === 'system' || next === 'light' || next === 'dark') setModeState(next);
  }, []);

  // Cycle order: system → light → dark → system
  const toggleMode = useCallback(() => {
    setModeState((prev) => (prev === 'system' ? 'light' : prev === 'light' ? 'dark' : 'system'));
  }, []);

  const value = useMemo(() => ({ mode, resolved, setMode, toggleMode }), [mode, resolved, setMode, toggleMode]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}
